<script>
    import { getSignedUrlPromise, s3url, refreshToken } from '../lib/ldcjs/aws_helper';
    import { fromCognitoIdentity } from "@aws-sdk/credential-provider-cognito-identity";
    import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
    import { S3Client, GetObjectCommand } from "@aws-sdk/client-s3";
    import { getSignedUrl } from "@aws-sdk/s3-request-presigner";

    refreshToken({
        fromCognitoIdentity,
        CognitoIdentityClient,
        S3Client,
        GetObjectCommand,
        getSignedUrl
    });

    let { admin = false, project_manager = false } = $props();

    let releases = $state([]);
    let loading = $state(true);
    let showForm = $state(false);
    let error = $state(null);

    let formData = $state({
        name: '',
        s3_path: '',
        description: '',
        md5sum: '',
        active: true
    });

    $effect(() => {
        loadReleases();
    });

    async function loadReleases() {
        loading = true;
        error = null;
        try {
            const response = await fetch('/releases.json');
            if (!response.ok) throw new Error('Failed to load releases');
            releases = await response.json();
        } catch (e) {
            error = e.message;
        } finally {
            loading = false;
        }
    }

    async function createRelease(e) {
        e.preventDefault();
        error = null;

        try {
            const response = await fetch('/releases', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': window.AUTH_TOKEN
                },
                body: JSON.stringify({ release: formData })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to create release');
            }

            formData = { name: '', s3_path: '', description: '', md5sum: '', active: true };
            showForm = false;
            await loadReleases();
        } catch (e) {
            error = e.message;
        }
    }

    async function downloadRelease(releaseId, s3Path) {
        try {
            // Parse S3 path (supports both "bucket/key" and "s3://bucket/key" formats)
            const cleanPath = s3Path.replace(/^s3:\/\//, '');
            const parts = cleanPath.split('/');
            const bucket = parts[0];
            const key = parts.slice(1).join('/');

            // Generate presigned URL on client side
            const url = await getSignedUrlPromise(bucket, key);

            // Record delivery
            await fetch(`/releases/${releaseId}/deliveries`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': window.AUTH_TOKEN
                }
            });

            // Open download URL
            window.open(url, '_blank');
        } catch (e) {
            error = e.message || 'Failed to download file';
        }
    }

    async function toggleActive(release) {
        try {
            const response = await fetch(`/releases/${release.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': window.AUTH_TOKEN
                },
                body: JSON.stringify({ release: { active: !release.active } })
            });

            if (!response.ok) throw new Error('Failed to update release');
            await loadReleases();
        } catch (e) {
            error = e.message;
        }
    }

    async function deleteRelease(releaseId, releaseName) {
        if (!confirm(`Are you sure you want to delete "${releaseName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`/releases/${releaseId}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': window.AUTH_TOKEN
                }
            });

            if (!response.ok) throw new Error('Failed to delete release');
            await loadReleases();
        } catch (e) {
            error = e.message;
        }
    }
</script>

<div class="container mx-auto py-8">
    {#if error}
        <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
        </div>
    {/if}

    <div class="flex justify-between items-center mb-6">
        <h1 class="text-3xl font-bold">Releases</h1>
        {#if admin || project_manager}
            <button
                onclick={() => showForm = !showForm}
                class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                {showForm ? 'Cancel' : '+ New Release'}
            </button>
        {/if}
    </div>

    {#if showForm && (admin || project_manager)}
        <div class="mb-6 p-6 bg-gray-100 rounded">
            <h2 class="text-xl font-bold mb-4">Create New Release</h2>
            <form onsubmit={createRelease}>
                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="name">
                        Name *
                    </label>
                    <input
                        id="name"
                        bind:value={formData.name}
                        required
                        class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight"
                        type="text"
                        placeholder="Release name" />
                </div>

                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="s3_path">
                        S3 Path *
                    </label>
                    <input
                        id="s3_path"
                        bind:value={formData.s3_path}
                        required
                        class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight"
                        type="text"
                        placeholder="bucket/path/to/file.zip or s3://bucket/path/to/file.zip" />
                    <p class="text-gray-600 text-xs italic mt-1">
                        Format: bucket/path/to/file or s3://bucket/path/to/file
                    </p>
                </div>

                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="md5sum">
                        MD5 Checksum
                    </label>
                    <input
                        id="md5sum"
                        bind:value={formData.md5sum}
                        class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight font-mono"
                        type="text"
                        placeholder="Optional MD5 checksum for verification" />
                </div>

                <div class="mb-4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="description">
                        Description
                    </label>
                    <textarea
                        id="description"
                        bind:value={formData.description}
                        rows="3"
                        class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight"
                        placeholder="Optional description"></textarea>
                </div>

                <div class="mb-4">
                    <label class="flex items-center">
                        <input
                            type="checkbox"
                            bind:checked={formData.active}
                            class="mr-2" />
                        <span class="text-gray-700 text-sm">Active (users can download)</span>
                    </label>
                </div>

                <div class="flex items-center justify-between">
                    <button
                        type="submit"
                        class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded">
                        Create Release
                    </button>
                </div>
            </form>
        </div>
    {/if}

    {#if loading}
        <div class="text-center py-12">
            <p class="text-gray-500">Loading releases...</p>
        </div>
    {:else if releases.length === 0}
        <div class="text-center py-12">
            <p class="text-gray-500">No releases available yet.</p>
        </div>
    {:else}
        <div class="space-y-4">
            {#each releases as release}
                <div class="bg-white shadow rounded-lg p-6">
                    <div class="flex justify-between items-start">
                        <div class="flex-1">
                            <h3 class="text-xl font-semibold">{release.name}</h3>
                            <p class="text-gray-600 text-sm mt-1">
                                S3 Path: <code class="bg-gray-100 px-2 py-1 rounded">{release.s3_path}</code>
                            </p>
                            {#if release.md5sum}
                                <p class="text-gray-600 text-sm mt-1">
                                    MD5: <code class="bg-gray-100 px-2 py-1 rounded font-mono text-xs">{release.md5sum}</code>
                                </p>
                            {/if}
                            {#if release.description}
                                <p class="text-gray-700 mt-2">{release.description}</p>
                            {/if}
                            <div class="mt-3 flex items-center space-x-4 text-sm text-gray-500">
                                <span><strong>Downloads:</strong> {release.download_count}</span>
                                <span><strong>Unique Users:</strong> {release.unique_downloaders}</span>
                                <span><strong>Created:</strong> {new Date(release.created_at).toLocaleDateString()}</span>
                                {#if !release.active}
                                    <span class="bg-red-100 text-red-800 px-2 py-1 rounded">Inactive</span>
                                {/if}
                            </div>
                        </div>

                        <div class="flex flex-col space-y-2 ml-4">
                            {#if release.active || admin}
                                <button
                                    onclick={() => downloadRelease(release.id, release.s3_path)}
                                    class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded whitespace-nowrap">
                                    Download
                                </button>
                            {/if}

                            {#if admin}
                                <button
                                    onclick={() => toggleActive(release)}
                                    class="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded whitespace-nowrap">
                                    {release.active ? 'Deactivate' : 'Activate'}
                                </button>

                                <button
                                    onclick={() => deleteRelease(release.id, release.name)}
                                    class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded whitespace-nowrap">
                                    Delete
                                </button>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>
